/**
 * components/grid/SimpleGrid.tsx — Reference grid (PLAN §2.18, T14).
 *
 * Grid on the X-Z plane at y=0 (SPEC AC-1) using THREE.GridHelper, sopra un
 * piano PIENO ("il foglio di carta", 24-09-2026): `GridHelper` disegna solo
 * linee, quindi senza un piano di riempimento lo sfondo del canvas si vede
 * identico dentro e fuori l'area della griglia — "l'ambiente si mischia col
 * foglio". Il piano (MeshBasicMaterial, non reagisce a luci/ombre — "lascia
 * stare le ombre") da' al foglio un colore SUO, distinto dallo sfondo canvas
 * (l'"ambiente" attorno), cosi' i due si separano visivamente a ogni angolo
 * di camera. Get size/divisions/colori from config (GRID_DEFAULTS +
 * COLORS.grid/gridFloor in T15): MUTE component, no calculation.*/

import { useMemo } from "react";
import * as THREE from "three";

// ─── Props (PLAN §2.18) ───────────────────────────────────────

export interface SimpleGridProps {
  /** Total grid side (mm) — default from config GRID_DEFAULTS.*/
  size: number;
  /** Number of divisions per side — default from config GRID_DEFAULTS.*/
  divisions: number;
  /** Line color (from config/colors.ts).*/
  color: string;
  /**
   * Colore del piano pieno sotto le linee ("il foglio di carta").*/
  floorColor: string;
  /**
   * Colore del bordo attorno al foglio (richiesta utente 24-09-2026: "quella
   * sensazione di foglio di carta" — un bordo netto, non diluito
   * dall'opacita' delle righe, rinforza il perimetro del foglio).*/
  borderColor: string;
  /** Line opacity (0..1).*/
  opacity?: number;
}

//─── Component ─────────────────────── ────────────────────────

/**
 * Render the reference grid on the X-Z plane at y=0: piano pieno (il
 * "foglio") leggermente sotto (y=-1mm, evita z-fighting con le linee) piu'
 * il `GridHelper` sopra. Center and line colors are the same (minimum
 * gradient for maintain technical reference without visual noise).*/
// Default 0.8 (24-09-2026, estetica "foglio di carta"): righe nitide come un
// foglio a quadretti, non diluite — col colore griglia gia' pensato per un
// buon contrasto sul foglio, non serve piu' trasparenza pesante.
export function SimpleGrid({ size, divisions, color, floorColor, borderColor, opacity = 0.8 }: SimpleGridProps) {
  const grid = useMemo(() => {
    const helper = new THREE.GridHelper(size, divisions, color, color);
    helper.position.y = 0;

    //Makes lines transparent (readable on dark background).
    helper.traverse((child) => {
      const material = (child as THREE.Line).material;
      if (material && !Array.isArray(material)) {
        material.transparent = true;
        material.opacity = opacity;
      }
    });

    return helper;
  }, [size, divisions, color, opacity]);

  //Bordo del foglio: solo il perimetro (4 lati), non un contorno per cella —
  //EdgesGeometry su un piano da' esattamente il rettangolo esterno.
  const border = useMemo(() => new THREE.EdgesGeometry(new THREE.PlaneGeometry(size, size)), [size]);

  return (
    <group>
      {/*Il "foglio": piano pieno, MeshBasicMaterial (unlit → non riceve/da'
      ombre, "lascia stare le ombre"). Leggermente sotto le linee per evitare
      z-fighting.*/}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -1, 0]}>
        <planeGeometry args={[size, size]} />
        <meshBasicMaterial color={floorColor} />
      </mesh>
      {/*Bordo del foglio, appena sopra il piano (y=-0.5) e sotto le righe
      (y=0): pieno (opacita' 1), non diluito come le righe interne.*/}
      <lineSegments geometry={border} rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.5, 0]}>
        <lineBasicMaterial color={borderColor} />
      </lineSegments>
      <primitive object={grid} />
    </group>
  );
}
